// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import GeoIP from '@app/features/app/state/GeoIP';
import Channels from '@app/features/channel/state/Channels';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import {Logger} from '@app/features/platform/utils/AppLogger';
import Sound from '@app/features/ui/state/Sound';
import Users from '@app/features/user/state/Users';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import CallInitiator from '@app/features/voice/state/CallInitiator';
import CallState from '@app/features/voice/state/CallState';
import RtcRegions from '@app/features/voice/state/RtcRegions';
import {AUTOMATIC_VOICE_REGION_ID} from '@konvo/constants/src/ChannelConstants';
import type {RtcRegionResponse} from '@konvo/schema/src/domains/channel/ChannelSchemas';

interface CallEligibilityResponse {
	ringable: boolean;
}

interface CallRingRequest {
	recipients?: Array<string>;
	latitude?: string;
	longitude?: string;
}

interface CallRegionPatch {
	region: string;
	latitude?: string;
	longitude?: string;
}

interface DirectCallStartAttempt {
	channelId: string;
	recipients: Array<string>;
	cancelled: boolean;
	ringPrepared: boolean;
	voiceStateSent: boolean;
	abortController: AbortController | null;
	preparation: Promise<void> | null;
	cleanup: Promise<void> | null;
}

const logger = new Logger('CallCommands');
const DIRECT_CALL_RING_TIMEOUT_MS = 10000;

let activeDirectCallStart: DirectCallStartAttempt | null = null;
let directCallPreparationQueue: Promise<void> = Promise.resolve();

function currentGeoCoordinates(): Pick<CallRingRequest, 'latitude' | 'longitude'> {
	const latitude = GeoIP.latitude;
	const longitude = GeoIP.longitude;
	return latitude && longitude ? {latitude, longitude} : {};
}

function callRingRequest(recipients?: Array<string>): CallRingRequest {
	return {
		...(recipients ? {recipients} : {}),
		...currentGeoCoordinates(),
	};
}

function stopRingingRequest(recipients?: Array<string>): Pick<CallRingRequest, 'recipients'> {
	return recipients ? {recipients} : {};
}

function channelRecipientIds(channelId: string, currentUserId: string): Array<string> {
	const channel = Channels.getChannel(channelId);
	return channel ? channel.recipientIds.filter((id) => id !== currentUserId) : [];
}

function initiatedRingingRecipients(channelId: string): Array<string> {
	const callRinging = CallState.getCall(channelId)?.ringing ?? [];
	const initiatedRecipients = CallInitiator.getInitiatedRecipients(channelId);
	const initiatedSet = initiatedRecipients.length > 0 ? new Set(initiatedRecipients) : null;
	if (initiatedSet && callRinging.length === 0) return initiatedRecipients;
	return initiatedSet ? callRinging.filter((userId) => initiatedSet.has(userId)) : callRinging;
}

function callRegionPatch(region: string): CallRegionPatch {
	return {
		region,
		...(region === AUTOMATIC_VOICE_REGION_ID ? currentGeoCoordinates() : {}),
	};
}

export async function checkCallEligibility(channelId: string): Promise<CallEligibilityResponse> {
	const response = await http.get<CallEligibilityResponse>(Endpoints.CHANNEL_CALL(channelId));
	return response.body ?? {ringable: false};
}

async function ringCallRecipients(
	channelId: string,
	recipients?: Array<string>,
	options?: {signal?: AbortSignal; timeoutMs?: number; retries?: number},
): Promise<void> {
	await http.post(Endpoints.CHANNEL_CALL_RING(channelId), {body: callRingRequest(recipients), ...options});
}

async function stopRingingCallRecipients(
	channelId: string,
	recipients?: Array<string>,
	options?: {timeoutMs?: number; retries?: number},
): Promise<void> {
	await http.post(Endpoints.CHANNEL_CALL_STOP_RINGING(channelId), {body: stopRingingRequest(recipients), ...options});
}

export async function ringParticipants(channelId: string, recipients?: Array<string>): Promise<void> {
	return ringCallRecipients(channelId, recipients);
}

export async function stopRingingParticipants(channelId: string, recipients?: Array<string>): Promise<void> {
	return stopRingingCallRecipients(channelId, recipients);
}

function directCallStartCancelledError(): Error {
	return Object.assign(new Error('Direct call start cancelled'), {name: 'AbortError'});
}

function isDirectCallStartCancelled(error: unknown): boolean {
	return error instanceof Error && error.name === 'AbortError';
}

function directCallStartOutcomeMayBeUnknown(error: unknown): boolean {
	return !(error instanceof HttpError) || error.status >= 500;
}

async function compensateDirectCallStart(attempt: DirectCallStartAttempt): Promise<void> {
	try {
		await stopRingingCallRecipients(attempt.channelId, attempt.recipients, {
			timeoutMs: DIRECT_CALL_RING_TIMEOUT_MS,
			retries: 0,
		});
	} catch (error) {
		logger.error('Failed to stop ringing after a cancelled direct call start:', error);
	}
}

async function prepareDirectCallStart(attempt: DirectCallStartAttempt): Promise<void> {
	if (attempt.cancelled || activeDirectCallStart !== attempt) throw directCallStartCancelledError();
	CallInitiator.markInitiated(attempt.channelId, attempt.recipients);
	const abortController = new AbortController();
	attempt.abortController = abortController;
	let compensated = false;
	try {
		await ringCallRecipients(attempt.channelId, attempt.recipients, {
			signal: abortController.signal,
			timeoutMs: DIRECT_CALL_RING_TIMEOUT_MS,
			retries: 0,
		});
		if (attempt.cancelled || activeDirectCallStart !== attempt) {
			await compensateDirectCallStart(attempt);
			compensated = true;
			throw directCallStartCancelledError();
		}
		attempt.ringPrepared = true;
	} catch (error) {
		CallInitiator.clearChannel(attempt.channelId);
		if (!compensated && directCallStartOutcomeMayBeUnknown(error)) {
			await compensateDirectCallStart(attempt);
		}
		throw error;
	} finally {
		attempt.abortController = null;
	}
}

function queueDirectCallStartPreparation(attempt: DirectCallStartAttempt): Promise<void> {
	const preparation = directCallPreparationQueue.then(() => prepareDirectCallStart(attempt));
	attempt.preparation = preparation;
	directCallPreparationQueue = preparation.catch(() => undefined);
	return preparation;
}

function cancelDirectCallStart(attempt: DirectCallStartAttempt): Promise<void> {
	if (attempt.voiceStateSent) return Promise.resolve();
	if (!attempt.cancelled) {
		attempt.cancelled = true;
		attempt.abortController?.abort();
		CallInitiator.clearChannel(attempt.channelId);
	}
	if (activeDirectCallStart === attempt) activeDirectCallStart = null;
	if (attempt.ringPrepared && !attempt.cleanup) {
		attempt.cleanup = directCallPreparationQueue.then(() => compensateDirectCallStart(attempt));
		directCallPreparationQueue = attempt.cleanup.catch(() => undefined);
	}
	if (attempt.cleanup) return attempt.cleanup;
	return attempt.preparation?.catch(() => undefined) ?? Promise.resolve();
}

function finishDirectCallStart(attempt: DirectCallStartAttempt): void {
	attempt.voiceStateSent = true;
	if (activeDirectCallStart === attempt) activeDirectCallStart = null;
}

export function startCall(channelId: string): void {
	const currentUser = Users.getCurrentUser();
	if (!currentUser) {
		return;
	}
	const recipients = channelRecipientIds(channelId, currentUser.id);
	if (recipients.length === 0) {
		logger.warn('Cannot start a direct call without another recipient', {channelId});
		return;
	}
	if (activeDirectCallStart?.channelId === channelId && !activeDirectCallStart.cancelled) return;
	if (activeDirectCallStart) void cancelDirectCallStart(activeDirectCallStart);
	const attempt: DirectCallStartAttempt = {
		channelId,
		recipients,
		cancelled: false,
		ringPrepared: false,
		voiceStateSent: false,
		abortController: null,
		preparation: null,
		cleanup: null,
	};
	activeDirectCallStart = attempt;
	void MediaEngine.connectToVoiceChannel(null, channelId, {
		shouldStartVoiceConnection: () => activeDirectCallStart === attempt && !attempt.cancelled,
		beforeVoiceStateConnect: () => queueDirectCallStartPreparation(attempt),
		onVoiceStateConnect: () => finishDirectCallStart(attempt),
		onVoiceConnectCancelled: (error) => {
			void cancelDirectCallStart(attempt);
			if (error && !isDirectCallStartCancelled(error)) {
				logger.error('Failed to prepare direct call before connecting:', error);
			}
		},
	}).catch((error) => {
		void cancelDirectCallStart(attempt);
		logger.error('Failed to start direct call:', error);
	});
}

export function joinCall(channelId: string): void {
	const currentUser = Users.getCurrentUser();
	if (!currentUser) {
		return;
	}
	CallState.clearPendingRinging(channelId, [currentUser.id]);
	Sound.stopIncomingRing();
	void MediaEngine.connectToVoiceChannel(null, channelId);
}

export async function leaveCall(channelId: string): Promise<void> {
	const currentUser = Users.getCurrentUser();
	if (!currentUser) {
		return;
	}
	const pendingStart = activeDirectCallStart?.channelId === channelId ? activeDirectCallStart : null;
	const pendingStartCleanup = pendingStart ? cancelDirectCallStart(pendingStart) : null;
	Sound.stopIncomingRing();
	const toStop = pendingStart ? [] : initiatedRingingRecipients(channelId);
	if (pendingStartCleanup) {
		await pendingStartCleanup;
	} else if (toStop.length > 0) {
		try {
			await stopRingingCallRecipients(channelId, toStop);
		} catch (error) {
			logger.error('Failed to stop ringing pending recipients:', error);
		}
	}
	CallInitiator.clearChannel(channelId);
	void MediaEngine.disconnectFromVoiceChannel('user');
}

export function rejectCall(channelId: string): void {
	const currentUser = Users.getCurrentUser();
	if (!currentUser) {
		return;
	}
	CallState.clearPendingRinging(channelId, [currentUser.id]);
	const connectedChannelId = MediaEngine.channelId;
	if (connectedChannelId === channelId) {
		void MediaEngine.disconnectFromVoiceChannel('user');
	}
	void stopRingingCallRecipients(channelId).catch((error) => {
		logger.error('Failed to stop ringing:', error);
	});
	CallInitiator.clearChannel(channelId);
}

export function ignoreCall(channelId: string): void {
	const currentUser = Users.getCurrentUser();
	if (!currentUser) {
		return;
	}
	CallState.clearPendingRinging(channelId, [currentUser.id]);
	void stopRingingCallRecipients(channelId, [currentUser.id]).catch((error) => {
		logger.error('Failed to stop ringing:', error);
	});
}

export async function fetchCallRegions(channelId: string): Promise<Array<RtcRegionResponse>> {
	const channel = Channels.getChannel(channelId);
	if (channel?.isPrivate()) {
		return RtcRegions.getRegions();
	}
	const response = await http.get<Array<RtcRegionResponse>>(Endpoints.CHANNEL_RTC_REGIONS(channelId));
	return response.body ?? [];
}

export async function updateCallRegion(channelId: string, region: string): Promise<void> {
	await http.patch(Endpoints.CHANNEL_CALL(channelId), {body: callRegionPatch(region)});
}
